import type { AutomergeUrl, Repo } from "@automerge/automerge-repo";
/**
 * Directory scope, shared by capture, restore and every historical read. A
 * checkpoint always targets a directory: `"/"` is the whole project, otherwise
 * a slash-separated path with no leading or trailing slash.
 */
import { invariant } from "ts-invariant";

import { listedEntries } from "../fs/dir-tree";
import {
  cleanSegments,
  collectEntries,
  isDirInode,
  isInlineDir,
  isLinkedDir,
  resolveInode,
  type TreeEntry,
} from "../fs/paths";
import type { DirectoryDoc, PlayFileInode } from "../fs/types";
import type { CheckpointContent } from "../project";

/**
 * Normalize a checkpoint directory path to canonical form: `"/"` for the root,
 * otherwise no leading/trailing slashes (e.g. `"a/b"`). Defensive against the
 * `undefined` `path` of legacy checkpoints (those normalize to root).
 */
export function normalizeDirPath(path: string | undefined): string {
  // Empty and `.` segments collapse (`docs//deep` is `docs/deep`), so every
  // later phase sees the one spelling; a path that could escape the tree
  // (`..`, a backslash) is never a scope.
  const segments = cleanSegments(path ?? "");
  invariant(segments, `not a directory path within the project: ${path}`);
  return segments.length === 0 ? "/" : segments.join("/");
}

/** True if `entryPath` lives at or under directory `dirPath` (normalized). */
export function isUnderDir(entryPath: string, dirPath: string): boolean {
  if (dirPath === "/") return true;
  return entryPath === dirPath || entryPath.startsWith(`${dirPath}/`);
}

/** `path` relative to `dir` (both normalized; `""` for `dir` itself), or
 *  undefined when `path` is not under `dir`. */
export function relativeToDir(path: string, dir: string): string | undefined {
  if (!isUnderDir(path, dir)) return undefined;
  if (path === dir) return "";
  return dir === "/" ? path : path.slice(dir.length + 1);
}

/** The in-scope leaf entries (files, not directories) under `path` that are
 *  inline in `files` itself; a linked directory's are not (see
 *  {@link walkScope}). */
export function filesUnderScope(
  files: Record<string, PlayFileInode>,
  path: string | undefined,
): TreeEntry[] {
  const dir = normalizeDirPath(path);
  let scoped = files;
  if (dir !== "/") {
    const root = resolveInode(files, dir);
    if (!root || !isInlineDir(root)) return [];
    scoped = root.files.entries;
  }
  return collectEntries(scoped, dir === "/" ? "" : dir).filter(
    (e) => !e.isDirectory,
  );
}

/**
 * The entries at `viewedPath` inside a captured `structure` rooted at the
 * checkpoint's own scope `cpPath`. The viewed path must be at or under the
 * scope; a root-scoped checkpoint viewed at `a/b` descends into its structure
 * rather than casting it wholesale. Empty when the directory did not exist at
 * capture (or nothing was captured).
 */
export function entriesAtViewedPath(
  structure: CheckpointContent["structure"],
  cpPath: string,
  viewedPath: string,
): Record<string, PlayFileInode> {
  if (structure === null) return {};
  const cpDir = normalizeDirPath(cpPath);
  const viewedDir = normalizeDirPath(viewedPath);
  let rootEntries: Record<string, PlayFileInode>;
  if (cpDir === "/") {
    rootEntries = structure as Record<string, PlayFileInode>;
  } else {
    const root = structure as PlayFileInode;
    rootEntries = isInlineDir(root) ? root.files.entries : {};
  }
  const relative = relativeToDir(viewedDir, cpDir);
  if (relative === undefined) return {};
  if (relative === "") return rootEntries;
  const inode = resolveInode(rootEntries, relative);
  return inode && isInlineDir(inode) ? inode.files.entries : {};
}

/** A file a {@link walkScope} found, and whether it was reached through a
 *  linked directory (so it is not in the project document itself). */
export interface ScopedFile extends TreeEntry {
  linked: boolean;
}

export interface ScopeWalk {
  /** The scope's own inode; `undefined` for the root, `null` when missing. */
  scope: PlayFileInode | null | undefined;
  /** The scope's inode is in a linked directory's document, not `files`. */
  scopeLinked: boolean;
  files: ScopedFile[];
  /** Every linked directory at or under the scope, by url: its heads, read
   *  in the same tick as the entries the walk took from it. */
  directories: Record<string, string[]>;
}

/**
 * Every file at or under `path`, through inline and linked directories alike,
 * plus the version of each linked directory crossed. Capture's enumeration:
 * the files it versions and the directory versions a historical read walks.
 * Ancestors of the scope are read live and not recorded. A directory already
 * on the walk's path (a cycle) is not descended; one linked at two paths is
 * walked at both, under one recorded version.
 */
export async function walkScope(
  repo: Repo,
  files: Record<string, PlayFileInode>,
  path: string | undefined,
  signal?: AbortSignal,
): Promise<ScopeWalk> {
  const dir = normalizeDirPath(path);
  const out: ScopeWalk = {
    scope: undefined,
    scopeLinked: false,
    files: [],
    directories: {},
  };
  const load = async (url: string, record: boolean) => {
    const handle = await repo.find<DirectoryDoc>(url as AutomergeUrl, {
      signal,
    });
    await handle.whenReady();
    const heads = handle.heads();
    const doc = handle.doc();
    invariant(heads && doc?.entries, `not a directory document: ${url}`);
    if (record) out.directories[url] = [...heads];
    return listedEntries(doc.entries);
  };

  let entries = files;
  const segs = dir === "/" ? [] : dir.split("/");
  for (const [i, seg] of segs.entries()) {
    const inode = entries[seg];
    if (i === segs.length - 1) out.scope = inode ?? null;
    if (!inode || !isDirInode(inode)) return out;
    if (isLinkedDir(inode) && i < segs.length - 1) out.scopeLinked = true;
    if (isLinkedDir(inode)) {
      entries = await load(inode.uri, i === segs.length - 1);
    } else {
      entries = inode.files.entries;
    }
  }

  const descend = async (
    at: Record<string, PlayFileInode>,
    prefix: string,
    above: ReadonlySet<string>,
    linked: boolean,
  ): Promise<void> => {
    for (const [name, inode] of Object.entries(at)) {
      const entryPath = prefix ? `${prefix}/${name}` : name;
      if (isLinkedDir(inode)) {
        if (above.has(inode.uri)) continue;
        const children = await load(inode.uri, true);
        await descend(
          children,
          entryPath,
          new Set([...above, inode.uri]),
          true,
        );
      } else if (isInlineDir(inode)) {
        await descend(inode.files.entries, entryPath, above, linked);
      } else {
        out.files.push({ path: entryPath, inode, isDirectory: false, linked });
      }
    }
  };
  const self = out.scope && isLinkedDir(out.scope) ? [out.scope.uri] : [];
  await descend(
    entries,
    dir === "/" ? "" : dir,
    new Set(self),
    self.length > 0,
  );
  return out;
}

/**
 * Every document a tree owns: its file bodies and linked directories, through
 * {@link walkScope}. A mount's unit isn't the tree's and isn't fetched, so an
 * unreachable unit can't stop GC or a delete.
 */
export async function treeDocuments(
  repo: Repo,
  files: Record<string, PlayFileInode>,
): Promise<string[]> {
  const walk = await walkScope(repo, files, "/");
  return [
    ...walk.files.map((f) => f.inode.uri),
    ...Object.keys(walk.directories),
  ];
}
